import { useState, useRef, useEffect } from "react";
import { Sparkles, Upload, Loader2, FileText, X } from "lucide-react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LeadTable from "@/components/LeadTable";
import { toast } from "sonner";

export default function EnrichCSV() {
  const [file, setFile] = useState(null);
  const [icp, setIcp] = useState("");
  const [campaignId, setCampaignId] = useState("none");
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const inputRef = useRef();

  useEffect(() => {
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  }, []);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (f && f.name.endsWith(".csv")) setFile(f);
    else toast.error("Please select a .csv file");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f && f.name.endsWith(".csv")) setFile(f);
    else toast.error("Please drop a .csv file");
  };

  const enrich = async () => {
    if (!file) { toast.error("Select a CSV file first"); return; }
    setLoading(true);
    setResults([]);
    try {
      const form = new FormData();
      form.append("file", file);
      if (icp) form.append("icp_description", icp);
      if (campaignId !== "none") form.append("campaign_id", campaignId);
      const token = localStorage.getItem("lf_token");
      const res = await fetch(`${API}/leads/enrich-csv`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Enrichment failed");
      }
      const data = await res.json();
      setResults(data);
      toast.success(`Enriched ${data.length} leads`);
    } catch (e) {
      toast.error(e.message || "Enrichment failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="label-mono mb-2">— Lead Generation</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">Enrich CSV</h1>
        <p className="text-[#5C5C66] mt-2">Upload a partial lead list — AI fills missing emails, titles, and ICP scores automatically.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        <div className="lg:col-span-2 bg-white border border-[#EAEAE3] rounded-2xl p-6 shadow-soft">
          {/* Drop zone */}
          <div
            className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors mb-5 ${
              file ? "border-[#002FA7] bg-[#002FA7]/5" : "border-[#EAEAE3] hover:border-[#002FA7]/40 hover:bg-[#FAFAF7]"
            }`}
            onClick={() => inputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            <input ref={inputRef} type="file" accept=".csv" className="hidden" onChange={handleFile} />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="h-8 w-8 text-[#002FA7]" />
                <div className="text-left">
                  <div className="font-medium text-[#002FA7]">{file.name}</div>
                  <div className="text-xs text-[#5C5C66]">{(file.size / 1024).toFixed(1)} KB</div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); setFile(null); }} className="ml-2 text-[#9A9AA3] hover:text-red-500">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <>
                <Upload className="h-10 w-10 text-[#9A9AA3] mx-auto mb-3" />
                <div className="font-medium mb-1">Drop your CSV here or click to browse</div>
                <div className="text-xs text-[#5C5C66]">Supports any CSV with name, company, or email columns</div>
              </>
            )}
          </div>

          <div className="mb-4">
            <Label className="label-mono">ICP description (optional but improves scoring)</Label>
            <Textarea
              value={icp}
              onChange={(e) => setIcp(e.target.value)}
              placeholder="e.g. SaaS companies with 50-500 employees, VP Sales or Marketing roles, US-based"
              className="mt-2 rounded-lg min-h-[80px] text-sm"
            />
          </div>

          <div className="mb-4">
            <Label className="label-mono">Save to campaign</Label>
            <Select value={campaignId} onValueChange={setCampaignId}>
              <SelectTrigger className="mt-2 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Don't save to campaign</SelectItem>
                {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <Button onClick={enrich} disabled={loading || !file} className="rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white" data-testid="enrich-btn">
            {loading
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enriching leads…</>
              : <><Sparkles className="h-4 w-4 mr-2" /> Enrich with AI</>}
          </Button>
        </div>

        <div className="space-y-4">
          <div className="bg-[#0A0A0F] text-white p-5 rounded-2xl relative overflow-hidden shadow-soft">
            <div className="absolute inset-0 dot-bg-dark opacity-40" />
            <div className="relative">
              <div className="label-mono !text-white/50 mb-3">— What it does</div>
              <ul className="space-y-2.5 text-sm">
                {[
                  "Fills missing email addresses",
                  "Infers job titles from context",
                  "Adds LinkedIn profile URLs",
                  "Scores leads by ICP fit (1–100)",
                  "Generates prospect summaries",
                  "Identifies likely pain points",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="text-[#FF5722] mt-0.5">→</span>
                    <span className="text-white/80">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="bg-white border border-[#EAEAE3] rounded-2xl p-5 shadow-soft text-sm">
            <div className="label-mono mb-2">— CSV format tips</div>
            <ul className="space-y-1.5 text-[#5C5C66]">
              <li>• Columns: <code className="bg-[#F4F4EE] px-1 rounded text-xs font-mono">name, company, email, title, location</code></li>
              <li>• Missing fields are inferred by AI</li>
              <li>• Max 50 rows per enrichment</li>
            </ul>
          </div>
        </div>
      </div>

      {loading && (
        <div className="bg-white border border-[#EAEAE3] rounded-2xl p-12 text-center shadow-soft">
          <Loader2 className="h-8 w-8 animate-spin text-[#002FA7] mx-auto mb-4" />
          <div className="font-display text-lg font-bold tracking-tighter">Enriching your leads…</div>
          <p className="text-[#5C5C66] text-sm mt-2">AI is filling in the gaps and scoring each lead.</p>
        </div>
      )}

      {results.length > 0 && !loading && (
        <div className="bg-white border border-[#EAEAE3] rounded-2xl shadow-soft overflow-hidden">
          <div className="p-5 border-b border-[#EAEAE3]">
            <div className="font-display text-lg font-bold tracking-tighter">{results.length} leads enriched</div>
            <div className="text-xs text-[#5C5C66] mt-0.5">
              {campaignId !== "none" ? "Saved to campaign" : "Leads added to your pipeline"}
            </div>
          </div>
          <LeadTable leads={results} onRefresh={() => {}} />
        </div>
      )}
    </div>
  );
}
